import {
  type Block,
  parseMarkdownBlocks,
  renderInline,
  buildCopyPayload,
} from "./TrainingTipsModal.helpers";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { api, type TrainingTipsOut } from "../lib/api";
import {
  buildCallRefMap,
  linkifyCallIdsInHtml,
  type CallRefMap,
} from "../lib/linkifyCallIds";
import { buildModuleCopyPayload } from "./trainingModuleCopy";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type TabKey = "tips" | "module";

// PET-656 — admin-only override options for the analysed-call cap.
// The first entry (100) is the default everyone gets; the larger caps
// are only offered to admins / super admins. Exported as the single
// source of truth the FE/BE parity test asserts against.
export const DEFAULT_MAX_CALLS = 100;
export const ADMIN_MAX_CALLS_OPTIONS = [100, 200, 1000] as const;

export interface TrainingTipsModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentName: string;
  rangeFrom: string | null;
  rangeTo: string;
  labels: string[];
  rangeLabel: string;
  // PET-656 — admins/supadmins get the sample-size selector and an
  // explicit Generate step; everyone else keeps the auto-generate
  // flow. FE gating is convenience only — the server clamps the cap
  // from the actor's role regardless.
  isAdmin: boolean;
}

export function TrainingTipsModal({
  open,
  onClose,
  agentId,
  agentName,
  rangeFrom,
  rangeTo,
  labels,
  rangeLabel,
  isAdmin,
}: TrainingTipsModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("tips");
  const [tipsCopied, setTipsCopied] = useState(false);
  const [moduleCopied, setModuleCopied] = useState(false);
  // Once the user fires generation, both Generate buttons stay
  // disabled for the rest of the modal session. "Try again" from the
  // error state grants exactly one more attempt — after that the
  // button disappears for this session.
  const [moduleLocked, setModuleLocked] = useState(false);
  const [moduleRetryUsed, setModuleRetryUsed] = useState(false);
  // PET-656 — admin-only analysed-call cap. Per-run by construction:
  // local state, reset to the default on close, never persisted.
  const [maxCalls, setMaxCalls] = useState<number>(DEFAULT_MAX_CALLS);

  const tipsMutation = useMutation<TrainingTipsOut, Error>({
    mutationKey: ["training-tips", agentId],
    mutationFn: () =>
      api.trainingTipsForAgent(agentId, {
        from: rangeFrom,
        to: rangeTo,
        labels,
        // Only admins can lift the cap; for everyone else this stays
        // null and the server clamps it regardless.
        max_calls: isAdmin ? maxCalls : null,
      }),
  });

  const moduleMutation = useMutation<
    { html: string },
    Error,
    { report_id: string }
  >({
    mutationKey: ["training-module", agentId],
    mutationFn: (body) => api.generateTrainingModule(body),
  });

  // PET-656 — prepare the Tips view each time the modal opens.
  // Non-admins have no choice to make, so generation starts
  // immediately (exactly as before). Admins first pick the
  // analysed-call count and then press Generate, so we leave them
  // on an idle "ready to generate" state instead of auto-starting.
  useEffect(() => {
    if (open) {
      setActiveTab("tips");
      setTipsCopied(false);
      setModuleCopied(false);
      setModuleLocked(false);
      setModuleRetryUsed(false);
      tipsMutation.reset();
      moduleMutation.reset();
      if (!isAdmin) {
        tipsMutation.mutate();
      }
    } else {
      tipsMutation.reset();
      moduleMutation.reset();
      setTipsCopied(false);
      setModuleCopied(false);
      setModuleLocked(false);
      setModuleRetryUsed(false);
      // PET-656 — reset the admin cap so the next open starts at the
      // default (the override is per-run and never persists).
      setMaxCalls(DEFAULT_MAX_CALLS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel) {
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusables[0] ?? panel).focus();
    }
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  // Build a fast lookup for call_id -> phone + ordinal.
  const refMap = useMemo<CallRefMap>(
    () => buildCallRefMap(tipsMutation.data?.call_refs),
    [tipsMutation.data],
  );

  // PET-655 Part B — rewrite raw Dialpad call IDs in the generated
  // module HTML into ``phone (ordinal)`` links, reusing the same refMap
  // as the tips report (the module is generated from that same report,
  // so the pool — and therefore the ordinals — are identical).
  const moduleHtml = useMemo(() => {
    const html = moduleMutation.data?.html;
    if (!html) return null;
    return linkifyCallIdsInHtml(html, refMap);
  }, [moduleMutation.data, refMap]);

  // Stamp the report with a generation timestamp. The ID comes from
  // the server (task #349) so the Training Module endpoint can look
  // the original report text back up by ``report_id`` alone.
  const stamp = useMemo(() => {
    const data = tipsMutation.data;
    if (!data) return null;
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const formatted =
      `${pad(d.getDate())}:${pad(d.getMonth() + 1)}:${d.getFullYear()} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { formatted, id: data.report_id };
  }, [tipsMutation.data]);

  // Collect referenced phone numbers in order of first appearance.
  const referenced = useMemo(() => {
    const data = tipsMutation.data;
    if (!data) return [] as { phone: string; calls: Set<string> }[];
    const order: string[] = [];
    const byPhone = new Map<string, Set<string>>();
    const re = /\b\d{15,19}\b/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(data.report)) !== null) {
      const info = refMap.get(m[0]);
      if (!info || !info.contact_phone || info.phone_ordinal == null) continue;
      let set = byPhone.get(info.contact_phone);
      if (!set) {
        set = new Set<string>();
        byPhone.set(info.contact_phone, set);
        order.push(info.contact_phone);
      }
      set.add(m[0]);
    }
    return order.map((phone) => ({ phone, calls: byPhone.get(phone)! }));
  }, [tipsMutation.data, refMap]);

  const startGenerate = useCallback(() => {
    if (moduleLocked) return;
    if (!tipsMutation.data || !stamp) return;
    setModuleLocked(true);
    setActiveTab("module");
    moduleMutation.reset();
    moduleMutation.mutate({ report_id: stamp.id });
  }, [moduleLocked, tipsMutation.data, stamp, moduleMutation]);

  const onTryAgain = useCallback(() => {
    // One-shot per modal session: ignore further clicks once consumed.
    if (moduleRetryUsed) return;
    if (!tipsMutation.data || !stamp) return;
    setModuleRetryUsed(true);
    moduleMutation.reset();
    moduleMutation.mutate({ report_id: stamp.id });
  }, [moduleRetryUsed, tipsMutation.data, stamp, moduleMutation]);

  if (!open) return null;

  const onCopyTips = async () => {
    const data = tipsMutation.data;
    if (!data) return;
    try {
      const { html, text } = buildCopyPayload(
        data.report,
        refMap,
        stamp,
        agentName,
      );
      await writeRichClipboard(html, text);
      setTipsCopied(true);
      window.setTimeout(() => setTipsCopied(false), 2000);
    } catch {
      // ignore — selection-copy still works in the modal
    }
  };

  const onCopyModule = async () => {
    const html = moduleMutation.data?.html;
    if (!html) return;
    try {
      const payload = buildModuleCopyPayload(html, refMap, stamp, agentName);
      await writeRichClipboard(payload.html, payload.text);
      setModuleCopied(true);
      window.setTimeout(() => setModuleCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  // PET-656 — kick off (or retry) report generation. Admins reach this
  // via the explicit Generate button; non-admins reach it automatically
  // on open and via Retry after an error.
  const runTips = () => {
    setTipsCopied(false);
    tipsMutation.reset();
    tipsMutation.mutate();
  };

  const tipsErrorMessage =
    tipsMutation.error?.message?.replace(/^HTTP \d+:\s*/, "") ?? "";
  const moduleErrorMessage =
    moduleMutation.error?.message?.replace(/^HTTP \d+:\s*/, "") ?? "";

  const node = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-lg border border-border bg-card shadow-xl focus:outline-none training-tips-modal"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div>
            <h2
              id={titleId}
              className="text-lg font-semibold text-foreground"
              style={{ fontFamily: "var(--app-font-serif)" }}
            >
              Training tips — {agentName}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {rangeLabel}
              {labels.length > 0
                ? ` · ${labels.length} label filter${labels.length === 1 ? "" : "s"}`
                : ""}
              {tipsMutation.data?.calls_used
                ? ` · ${tipsMutation.data.calls_used} call${tipsMutation.data.calls_used === 1 ? "" : "s"} analysed${tipsMutation.data.truncated ? " (oldest truncated)" : ""}`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* PET-656 — admin-only override to raise the analysed-call
                count above the default 100. Hidden entirely for
                non-admins; the server clamps regardless. */}
            {isAdmin ? (
              <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span className="whitespace-nowrap">Calls analysed</span>
                <select
                  value={maxCalls}
                  onChange={(e) => {
                    setMaxCalls(Number(e.target.value));
                    // Changing the count returns the admin to the idle
                    // "ready to generate" state so they explicitly start
                    // a fresh run rather than processing kicking off on
                    // its own.
                    setTipsCopied(false);
                    setActiveTab("tips");
                    moduleMutation.reset();
                    tipsMutation.reset();
                  }}
                  disabled={tipsMutation.isPending}
                  className="rounded border border-input bg-card text-foreground text-xs font-medium normal-case tracking-normal px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  {ADMIN_MAX_CALLS_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n === DEFAULT_MAX_CALLS ? `${n} (default)` : `Up to ${n}`}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded border border-border bg-background text-foreground text-sm font-medium px-3 py-1.5 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Close
            </button>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Training tips views"
          className="flex items-center gap-1 border-b border-border px-5 pt-2"
        >
          <TabButton
            active={activeTab === "tips"}
            onClick={() => setActiveTab("tips")}
            label="Training Tips"
          />
          <TabButton
            active={activeTab === "module"}
            onClick={() => setActiveTab("module")}
            label="Training Module"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeTab === "tips" ? (
            <section className="relative">
              {tipsMutation.data ? (
                <div className="absolute right-0 top-0">
                  <button
                    type="button"
                    onClick={onCopyTips}
                    className="rounded border border-green-700 bg-green-600 text-white text-xs font-medium px-2.5 py-1 hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                  >
                    {tipsCopied ? "Copied" : "Copy"}
                  </button>
                </div>
              ) : null}

              {tipsMutation.isPending ? (
                <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                  <div className="h-8 w-8 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
                  <div>Generating coaching report…</div>
                  <div className="text-xs text-muted-foreground/80">
                    This usually takes 10–30 seconds.
                  </div>
                </div>
              ) : tipsMutation.error ? (
                <div className="rounded border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  <div className="font-medium">
                    Couldn't generate the report.
                  </div>
                  <div className="mt-1 text-destructive/90">
                    {tipsErrorMessage || "Unknown error."}
                  </div>
                  <button
                    type="button"
                    onClick={runTips}
                    className="mt-3 rounded bg-destructive text-destructive-foreground text-sm font-medium px-3 py-1.5 hover:bg-destructive/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                  >
                    Retry
                  </button>
                </div>
              ) : tipsMutation.data ? (
                <div className="pt-8">
                  <h3
                    className="text-base font-semibold text-foreground mb-2"
                    style={{ fontFamily: "var(--app-font-serif)" }}
                  >
                    Training tips for {agentName}
                  </h3>
                  {stamp ? (
                    <div className="mb-3 rounded border border-border bg-muted/30 px-3 py-2 text-xs text-foreground/80">
                      <div>
                        <span className="font-semibold">Generated:</span>{" "}
                        {stamp.formatted}
                      </div>
                      <div>
                        <span className="font-semibold">Report ID:</span>{" "}
                        <code className="rounded bg-muted/60 px-1 py-0.5">
                          {stamp.id}
                        </code>
                      </div>
                    </div>
                  ) : null}
                  <TrainingTipsMarkdown
                    markdown={tipsMutation.data.report}
                    refMap={refMap}
                  />
                  {referenced.length > 0 ? (
                    <section className="mt-6 border-t border-border pt-3">
                      <h4 className="text-sm font-semibold text-foreground mb-2">
                        Referenced phone numbers
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/90">
                        {referenced.map(({ phone, calls }) => (
                          <li key={phone}>
                            {phone} — {calls.size} call
                            {calls.size === 1 ? "" : "s"} referenced
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                  <div className="mt-6 flex justify-center border-t border-border pt-4">
                    <button
                      type="button"
                      onClick={startGenerate}
                      disabled={moduleLocked}
                      className="rounded bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    >
                      Generate Training Module
                    </button>
                  </div>
                </div>
              ) : isAdmin ? (
                // PET-656 — admins choose the analysed-call count above
                // and then explicitly start generation here, so
                // processing never begins before they've verified their
                // selection.
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                  <p className="text-sm text-muted-foreground max-w-md">
                    Choose how many recent calls to analyse with the{" "}
                    <span className="font-medium text-foreground">
                      Calls analysed
                    </span>{" "}
                    selector above, then generate the coaching report.
                  </p>
                  <button
                    type="button"
                    onClick={runTips}
                    className="rounded bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                  >
                    Generate report
                  </button>
                </div>
              ) : null}
            </section>
          ) : (
            <section className="relative">
              {moduleMutation.data ? (
                <div className="absolute right-0 top-0">
                  <button
                    type="button"
                    onClick={onCopyModule}
                    className="rounded border border-green-700 bg-green-600 text-white text-xs font-medium px-2.5 py-1 hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                  >
                    {moduleCopied ? "Copied" : "Copy"}
                  </button>
                </div>
              ) : null}

              {moduleMutation.isPending ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-muted-foreground">
                  <div className="h-8 w-8 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
                  <div>Generating training module…</div>
                </div>
              ) : moduleMutation.error ? (
                <div className="rounded border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  <div className="font-medium">
                    Couldn't generate the training module.
                  </div>
                  <div className="mt-1 text-destructive/90">
                    {moduleErrorMessage || "Unknown error."}
                  </div>
                  {moduleRetryUsed ? null : (
                    <button
                      type="button"
                      onClick={onTryAgain}
                      className="mt-3 rounded bg-destructive text-destructive-foreground text-sm font-medium px-3 py-1.5 hover:bg-destructive/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    >
                      Try again
                    </button>
                  )}
                </div>
              ) : moduleMutation.data ? (
                <div className="pt-8">
                  <h3
                    className="text-base font-semibold text-foreground mb-2"
                    style={{ fontFamily: "var(--app-font-serif)" }}
                  >
                    Training Module for {agentName}
                  </h3>
                  {stamp ? (
                    <div className="mb-3 rounded border border-border bg-muted/30 px-3 py-2 text-xs text-foreground/80">
                      <div>
                        <span className="font-semibold">Generated:</span>{" "}
                        {stamp.formatted}
                      </div>
                      <div>
                        <span className="font-semibold">
                          Ref TT Report ID:
                        </span>{" "}
                        <code className="rounded bg-muted/60 px-1 py-0.5">
                          {stamp.id}
                        </code>
                      </div>
                    </div>
                  ) : null}
                  <div
                    className="training-module-html"
                    dangerouslySetInnerHTML={{
                      __html: moduleHtml ?? moduleMutation.data.html,
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                  <p className="text-sm text-muted-foreground max-w-md">
                    Generate a practical coaching module based on this
                    assessment.
                  </p>
                  <button
                    type="button"
                    onClick={startGenerate}
                    disabled={moduleLocked || !tipsMutation.data}
                    className="rounded bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                  >
                    Generate Training Module
                  </button>
                </div>
              )}
            </section>
          )}
        </div>

        {tipsMutation.data ? (
          <div className="border-t border-border px-5 py-2 text-[11px] text-muted-foreground">
            Generated by {tipsMutation.data.model}. Not saved — closing this
            window discards the report.
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        "rounded-t border-b-2 px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {label}
    </button>
  );
}

async function writeRichClipboard(html: string, text: string): Promise<void> {
  const nav = navigator as Navigator & {
    clipboard: Clipboard & {
      write?: (items: ClipboardItem[]) => Promise<void>;
    };
  };
  const CI: typeof ClipboardItem | undefined =
    typeof ClipboardItem !== "undefined" ? ClipboardItem : undefined;
  if (CI && nav.clipboard?.write) {
    const item = new CI({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([text], { type: "text/plain" }),
    });
    await nav.clipboard.write([item]);
  } else {
    await navigator.clipboard.writeText(text);
  }
}

// Exported for tests (thematic breaks — the renderer must consume
// markdown the model emits but the report prompt never asked for).
export function TrainingTipsMarkdown({
  markdown,
  refMap,
}: {
  markdown: string;
  refMap: CallRefMap;
}) {
  const blocks = parseMarkdownBlocks(markdown);
  return (
    <article className="text-sm leading-relaxed text-foreground space-y-3">
      {blocks.map((b, i) => renderBlock(b, i, refMap))}
    </article>
  );
}

function renderBlock(b: Block, key: number, refMap: CallRefMap) {
  switch (b.kind) {
    case "h2":
      return (
        <h3
          key={key}
          className="mt-2 text-base font-semibold text-foreground border-b border-border pb-1"
          style={{ fontFamily: "var(--app-font-serif)" }}
        >
          {renderInline(b.text, refMap)}
        </h3>
      );
    case "h3":
      return (
        <h4 key={key} className="mt-1 text-sm font-semibold text-foreground">
          {renderInline(b.text, refMap)}
        </h4>
      );
    case "p":
      return (
        <p key={key} className="text-foreground/90">
          {renderInline(b.text, refMap)}
        </p>
      );
    case "ul":
      return (
        <ul key={key} className="list-disc pl-5 space-y-1 text-foreground/90">
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it, refMap)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="list-decimal pl-5 space-y-1 text-foreground/90">
          {b.items.map((it, j) => (
            <li key={j}>{renderInline(it, refMap)}</li>
          ))}
        </ol>
      );
    case "code":
      return (
        <pre
          key={key}
          className="overflow-x-auto rounded bg-muted/50 p-3 text-xs text-foreground"
        >
          <code>{b.text}</code>
        </pre>
      );
    case "table":
      return (
        <div key={key} className="overflow-x-auto">
          <table className="min-w-full text-xs border border-border rounded">
            <thead className="bg-muted/40">
              <tr>
                {b.header.map((h, j) => (
                  <th
                    key={j}
                    className="text-left font-semibold px-2 py-1.5 border-b border-border"
                  >
                    {renderInline(h, refMap)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri} className="odd:bg-card even:bg-muted/10">
                  {r.map((c, ci) => (
                    <td
                      key={ci}
                      className="px-2 py-1.5 border-b border-border align-top"
                    >
                      {renderInline(c, refMap)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
